import { Store } from '@/core/entities/store';

/**
 * Función para ordenar códigos de tienda de forma ascendente.
 * Los dígitos van antes que las letras, comparación case-insensitive.
 * El orden es ascendente por el código mostrado al inicio de cada opción (el texto antes del primer separador "-").
 *
 * Ejemplos válidos: 101 < 102 < 109 < 110 < 116 < 117 < 1D1 < 301.
 *
 * @param a - Primera tienda a comparar.
 *
 * @param b - Segunda tienda a comparar.
 *
 * @returns Número negativo si a < b, positivo si a > b, 0 si son iguales.
 */
export const sortStoresByCode = (a: Store, b: Store): number => {
  const codeA = a.organizationId || '';
  const codeB = b.organizationId || '';

  const getCode = (fullCode: string): string => {
    const parts = fullCode.split('-');
    return parts[0].trim();
  };

  const sortCodeA = getCode(codeA);
  const sortCodeB = getCode(codeB);

  const maxLength = Math.max(sortCodeA.length, sortCodeB.length);

  for (let i = 0; i < maxLength; i++) {
    const charA = sortCodeA[i] || '';
    const charB = sortCodeB[i] || '';

    const isDigitA = /\d/.test(charA);
    const isDigitB = /\d/.test(charB);

    if (isDigitA && !isDigitB) return -1;
    if (!isDigitA && isDigitB) return 1;

    if (isDigitA && isDigitB) {
      const numA = parseInt(charA, 10);
      const numB = parseInt(charB, 10);
      if (numA !== numB) return numA - numB;
    }

    if (!isDigitA && !isDigitB) {
      const lowerA = charA.toLowerCase();
      const lowerB = charB.toLowerCase();
      if (lowerA !== lowerB) return lowerA.localeCompare(lowerB);
    }
  }

  return sortCodeA.length - sortCodeB.length;
};

/**
 * Función para ordenar opciones de dropdown por código de tienda de forma ascendente.
 * Los dígitos van antes que las letras, comparación case-insensitive.
 * El orden es ascendente por el código mostrado al inicio de cada label (el texto antes del primer separador "-").
 *
 * @param options - Array de opciones con formato { label: string; value: string }.
 *
 * @returns Array ordenado de opciones.
 */
export const sortStoreOptionsByCode = (
  options: { label: string; value: string }[]
): { label: string; value: string }[] => {
  return options.sort((a, b) => {
    const getCode = (fullLabel: string): string => {
      const parts = fullLabel.split('-');
      return parts[0].trim();
    };

    const sortCodeA = getCode(a.label);
    const sortCodeB = getCode(b.label);

    const maxLength = Math.max(sortCodeA.length, sortCodeB.length);

    for (let i = 0; i < maxLength; i++) {
      const charA = sortCodeA[i] || '';
      const charB = sortCodeB[i] || '';

      const isDigitA = /\d/.test(charA);
      const isDigitB = /\d/.test(charB);

      if (isDigitA && !isDigitB) return -1;
      if (!isDigitA && isDigitB) return 1;

      if (isDigitA && isDigitB) {
        const numA = parseInt(charA, 10);
        const numB = parseInt(charB, 10);
        if (numA !== numB) return numA - numB;
      }

      if (!isDigitA && !isDigitB) {
        const lowerA = charA.toLowerCase();
        const lowerB = charB.toLowerCase();
        if (lowerA !== lowerB) return lowerA.localeCompare(lowerB);
      }
    }

    return sortCodeA.length - sortCodeB.length;
  });
};
